import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { getFeaturedProducts, Product } from '../../data/productsLoader';
import { Button } from '../ui/button';

interface FeaturedCollection {
  title: string;
  subtitle: string;
  description: string;
  products: Product[];
  accent: string;
  shellClassName: string;
  cardClassName: string;
  badgeClassName: string;
  buttonClassName: string;
}

function MetricPill({ label, value, className }: { label: string; value?: string; className: string }) {
  return (
    <div className={`rounded-full px-3 py-2 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 mb-1">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function EditorialHero({ collection }: { collection: FeaturedCollection }) {
  const product = collection.products[0];

  if (!product) {
    return null;
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className={`group overflow-hidden rounded-[2.25rem] ${collection.shellClassName}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] min-h-[560px]">
        <div className="relative z-10 flex flex-col justify-between p-8 md:p-10 lg:p-12">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className={`inline-flex rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${collection.badgeClassName}`}>
                {collection.subtitle}
              </span>
              <span className="inline-flex rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                Nuevo referente
              </span>
            </div>

            <div className="max-w-xl">
              <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5">
                {product.name}
              </h3>
              <p className="text-lg md:text-xl text-white/75 leading-relaxed mb-4">
                {product.slogan}
              </p>
              <p className="text-base md:text-lg text-white/60 leading-relaxed">
                {collection.description}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
              <MetricPill label="Autonomia" value={product.specs.range} className="bg-white/8 text-white backdrop-blur-sm" />
              <MetricPill label="Potencia" value={product.specs.motor} className="bg-white/8 text-white backdrop-blur-sm" />
              <MetricPill label="Peso" value={product.specs.weight} className="bg-white/8 text-white backdrop-blur-sm" />
              <MetricPill label="Serie" value={product.lineName} className="bg-white/8 text-white backdrop-blur-sm" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100">
                <Link to={product.link}>
                  Ver detalles
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/20 bg-transparent text-white hover:bg-white/10">
                <a href={product.shopUrl} target="_blank" rel="noopener noreferrer">
                  Comprar en la tienda
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="relative min-h-[340px] lg:min-h-full overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.2),transparent_38%),radial-gradient(circle_at_70%_75%,rgba(255,200,110,0.18),transparent_32%)]" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center p-10 md:p-14 lg:p-16">
            <img
              src={product.image}
              alt={product.name}
              className="relative z-10 w-full max-w-[640px] h-full object-contain drop-shadow-[0_40px_90px_rgba(0,0,0,0.45)] transition-transform duration-700 group-hover:scale-[1.04] group-hover:-rotate-1"
            />
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function ProductCard({
  product,
  collection,
  delay,
  compact = false
}: {
  product: Product;
  collection: FeaturedCollection;
  delay: number;
  compact?: boolean;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay }}
      className={`group overflow-hidden rounded-[1.9rem] ${collection.cardClassName} border border-black/5 shadow-[0_18px_60px_rgba(0,0,0,0.06)]`}
    >
      <div className={`relative ${compact ? 'aspect-[4/3]' : 'aspect-[4/3] md:aspect-[5/4]'} overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.45),transparent_28%),radial-gradient(circle_at_75%_75%,rgba(255,255,255,0.18),transparent_24%)]" />
        <img
          src={product.image}
          alt={product.name}
          className="relative z-10 w-full h-full object-contain p-6 md:p-8 transition-transform duration-700 group-hover:scale-105 group-hover:-rotate-1"
        />
      </div>

      <div className="p-6 md:p-7">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${collection.badgeClassName}`}>
            {collection.accent}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
            {product.lineName}
          </span>
        </div>

        <h4 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h4>
        <p className="text-gray-600 leading-relaxed mb-5 min-h-[3.5rem]">{product.slogan}</p>

        <div className="grid grid-cols-3 gap-2 mb-6">
          <MetricPill label="Autonomia" value={product.specs.range} className="bg-white/70 text-gray-900" />
          <MetricPill label="Potencia" value={product.specs.motor} className="bg-white/70 text-gray-900" />
          <MetricPill label="Peso" value={product.specs.weight} className="bg-white/70 text-gray-900" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild variant="outline" className="bg-transparent">
            <Link to={product.link}>Ver detalles</Link>
          </Button>
          <Button asChild className={collection.buttonClassName}>
            <a href={product.shopUrl} target="_blank" rel="noopener noreferrer">
              Tienda
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

export function FeaturedProducts() {
  const featured = getFeaturedProducts();

  const collections: FeaturedCollection[] = [
    {
      title: 'Ultimo lanzamiento',
      subtitle: 'La bestia off-road',
      description: 'Nuestro lanzamiento mas potente para quienes buscan sensaciones off-road, diseno imponente y prestaciones de alto nivel.',
      products: [featured.latestRelease].filter(Boolean) as Product[],
      accent: 'Lanzamiento',
      shellClassName: 'bg-[#111111] border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.18)]',
      cardClassName: 'bg-white',
      badgeClassName: 'bg-[#f3c76c] text-black',
      buttonClassName: 'bg-black text-white hover:bg-gray-900'
    },
    {
      title: 'El mas urbano',
      subtitle: 'Ligero y agil',
      description: 'Patinetes ligeros, agiles y faciles de llevar para moverte cada dia por la ciudad con comodidad.',
      products: [
        featured.urbanBest.zfoxMax,
        featured.urbanBest.zfoxPlus,
        featured.urbanBest.zlionPro
      ].filter(Boolean) as Product[],
      accent: 'Ciudad',
      shellClassName: 'bg-transparent',
      cardClassName: 'bg-[#f4f0e8]',
      badgeClassName: 'bg-black text-white',
      buttonClassName: 'bg-black text-white hover:bg-gray-900'
    },
    {
      title: 'Alta autonomia',
      subtitle: 'Para largos recorridos',
      description: 'Modelos pensados para recorrer mas distancia con estabilidad, comodidad y confianza en trayectos largos.',
      products: [
        featured.longRange.zrinoDuoMax,
        featured.longRange.zcougarDuo
      ].filter(Boolean) as Product[],
      accent: 'Autonomia',
      shellClassName: 'bg-transparent',
      cardClassName: 'bg-[#eef3ed]',
      badgeClassName: 'bg-[#18392b] text-white',
      buttonClassName: 'bg-[#18392b] text-white hover:bg-[#10271d]'
    }
  ];

  const [heroCollection, urbanCollection, rangeCollection] = collections;

  return (
    <section className="py-28 bg-[linear-gradient(180deg,#fff_0%,#fbf7ef_42%,#fff_100%)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 right-[8%] h-72 w-72 rounded-full bg-[#f0dcc0]/40 blur-3xl" />
        <div className="absolute top-[38%] -left-16 h-64 w-64 rounded-full bg-[#dde6d9]/50 blur-3xl" />
        <div className="absolute bottom-0 right-[16%] h-56 w-56 rounded-full bg-[#ebe0d1]/50 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mb-16">
          <motion.span
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-bold tracking-[0.18em] uppercase text-primary block mb-4"
          >
            Modelos destacados
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6"
          >
            Elige el Zwheel que mejor encaja con tu forma de moverte
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-gray-600 leading-relaxed"
          >
            Descubre nuestros modelos recomendados en tres perfiles claros: nuevo lanzamiento, uso urbano y larga autonomia.
          </motion.p>
        </div>

        <EditorialHero collection={heroCollection} />

        <div className="mt-20 space-y-20">
          <section>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div className="max-w-2xl">
                <span className="text-sm font-bold tracking-[0.18em] uppercase text-gray-500 block mb-3">
                  {urbanCollection.subtitle}
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{urbanCollection.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{urbanCollection.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {urbanCollection.products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  collection={urbanCollection}
                  delay={index * 0.08}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div className="max-w-2xl">
                <span className="text-sm font-bold tracking-[0.18em] uppercase text-gray-500 block mb-3">
                  {rangeCollection.subtitle}
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{rangeCollection.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{rangeCollection.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {rangeCollection.products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  collection={rangeCollection}
                  delay={index * 0.1}
                  compact
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
